<template>
    <span
        :class="disabled ? 'module-disabled-struct' : ''"
        :uk-tooltip="
            disabled
                ? 'title:' + $t('x.module.disabled.hint', {x: $t('swifter')})
                : ''
        ">
        <span
            :class="
                disabled ? 'module-disabled uk-height-1-1' : 'uk-height-1-1'
            ">
            <LoadingStruct :is-loading="loading" is-animation-disabled>
                <LabelWithError :label="$t('returnChannel.activate')" is-bold />
                <div class="uk-margin-small-bottom" />
                <SwitchButton
                    v-model="active"
                    @input="handleOverrideFee"
                    is-small
                    :id="'returnChannelActive'"
                    is-label-hidden
                    :label="$t('override')" />

                <div class="uk-margin-medium-top" />
                <LabelWithError :label="$t('used.storage.size')" is-bold />
                <div class="uk-margin-small-bottom" />

                <Translate
                    :parameter="{
                        used: getParsedBytes(stats.sizeUsageInBytes),
                        total: getParsedBytes(stats.sizeLimitInBytes),
                    }"
                    translation-key="x.of.total.x.used" />
                <progress
                    :value="stats.sizeUsedInPercentage"
                    class="uk-progress"
                    max="100" />

                <InfoBox v-if="isSizeLimitReached" icon="warning" mode="danger">
                    <Translate translation-key="storage.size.limit.reached" />
                </InfoBox>

                <div class="uk-margin-medium-top" />

                <div class="uk-margin-top" />
                <div v-if="!computedList || computedList.length === 0">
                    {{ $t('none.x', {x: $t('files')}) }}
                </div>
                <div v-else class="uk-overflow-auto">
                    <table
                        class="uk-table uk-table-middle uk-table-divider uk-table-hover">
                        <thead>
                            <tr>
                                <th class="uk-table-shrink">
                                    {{ $t('createdAt') }}
                                </th>

                                <th class="uk-table-shrink">
                                    {{ $t('title') }}
                                </th>

                                <th class="uk-table-shrink">
                                    {{ $t('emailAddress') }}
                                </th>

                                <th class="uk-table-shrink">
                                    {{ $t('name') }}
                                </th>

                                <th class="uk-table-shrink">
                                    {{ $t('size') }}
                                </th>

                                <th class="uk-table-shrink">
                                    {{ $t('downloaded') }}
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                v-for="(gallery, key) in computedList"
                                :key="'transfer-' + key"
                                class="uk-text-nowrap">
                                <td>
                                    <Icon
                                        class="uk-margin-small-right-x"
                                        icon="clock" />
                                    {{
                                        getFormattedDateTimeByDateString(
                                            gallery.createdAt
                                        )
                                    }}
                                </td>

                                <td>
                                    <Icon
                                        class="uk-margin-small-right-x"
                                        icon="hashtag" />
                                    {{ gallery.title }}
                                </td>

                                <td>
                                    <Icon
                                        class="uk-margin-small-right-x"
                                        icon="mail" />
                                    {{ gallery.email }}
                                </td>

                                <td>
                                    <Icon
                                        class="uk-margin-small-right-x"
                                        icon="user" />
                                    {{ gallery.name }}
                                </td>

                                <td>
                                    <Icon
                                        class="uk-margin-small-right-x"
                                        icon="database" />
                                    {{ getParsedBytes(gallery.sizeInBytes) }}
                                </td>

                                <td>
                                    <span v-if="gallery.downloadedAt">
                                        <Icon
                                            class="uk-margin-small-right-x"
                                            icon="past" />
                                        {{
                                            getFormattedDateTimeByDateString(
                                                gallery.downloadedAt
                                            )
                                        }}
                                    </span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="uk-text-center uk-margin-top">
                        <Button
                            v-if="visibleCount < computedListCount"
                            mode="default"
                            has-action
                            :action="showMore">
                            <Translate translation-key="show.more" />
                        </Button>
                    </div>
                </div>
            </LoadingStruct>
        </span>
    </span>
</template>

<script>
import axios from 'axios';
import LoadingStruct from '@/views/LoadingStruct.vue';
import Icon from '@/components/generic/Icon.vue';
import LabelWithError from '@/components/generic/LabelWithError.vue';
import Translate from '@/components/Translate.vue';
import InfoBox from '@/components/generic/InfoBox.vue';
import pretty from 'pretty-time';
import SwitchButton from '@/components/generic/SwitchButton.vue';
import UIkit from 'uikit';
import Button from '@/components/generic/Button.vue';

export default {
    name: 'TransfersChannelForm',
    components: {
        Button,
        SwitchButton,
        InfoBox,
        Translate,
        LabelWithError,
        Icon,
        LoadingStruct,
    },
    props: {
        disabled: Boolean,
        articleLists: Array,
        settings: Object,
    },
    data() {
        return {
            // data
            stats: {
                sizeUsageInBytes: 0,
                sizeLimitInBytes: 0,
                sizeUsedInPercentage: 0,
            },
            error: null,
            loading: true,

            active: false,

            visibleCount: 25,
        };
    },
    computed: {
        isSizeLimitReached() {
            return this.stats.sizeUsedInPercentage >= 100;
        },
        computedList() {
            const list = this.settings.swifts || [];

            return [...list].reverse().slice(0, this.visibleCount);
        },
        computedListCount() {
            const list = this.settings.swifts || [];
            return list.length;
        },
    },
    created() {
        this.active = this.settings.transfer.channelEnabled;

        if (this.disabled) {
            this.loading = false;
            return;
        }

        this.actionGetStats();
    },
    methods: {
        showMore() {
            this.visibleCount += 25;
        },
        pretty,
        handleOverrideFee(val) {
            const previousVal = JSON.parse(JSON.stringify(val));

            let http = axios.create();
            http.put(this.apiUrl + '/manage-transfers/channel', {
                enable: val,
            })
                .then(() => {})
                .catch((e) => {
                    this.checkGenericError(e);

                    this.active = previousVal;

                    UIkit.notification({
                        message: this.$t('unexpected.error'),
                        status: 'error',
                        pos: 'top-center',
                        timeout: this.notificationTimeout,
                    });
                });
        },
        actionGetStats() {
            let http = axios.create();

            http.get(this.apiUrl + '/manage-transfers/stats')
                .then((response) => {
                    this.stats = response.data;
                    this.stats.sizeUsedInPercentage =
                        (this.stats.sizeUsageInBytes /
                            this.stats.sizeLimitInBytes) *
                        100.0;

                    this.error = null;
                    this.loading = false;
                })
                .catch((e) => {
                    this.checkGenericError(e);

                    this.error = e;
                    this.loading = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.uk-margin-small-right-x {
    margin-right: 2px;
}

.width-action {
    width: 70px;
}

.generated-title {
    text-transform: uppercase;
    font-size: 48px;
    font-weight: 500;
}

.thumbnail-height {
    height: 100px;
}

.uk-table th {
    white-space: nowrap;
}

.uk-progress {
    border-radius: 0 !important;
}

.uk-progress::-webkit-progress-value {
    background-color: $theme-background !important;
}

.uk-progress::-moz-progress-bar {
    background-color: $theme-background !important;
}

.dragover {
    border-color: $info-color;
}

.folder-name {
    margin-top: 3px;
    font-size: 16px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.folder-sub {
    font-size: 15px;
}

.favicon-folder {
    max-width: 100px;
    max-height: 100px;
}
</style>
